import MeetExpertImage from "../../../../assets/icons/MeetExpertImg.svg";
import ConfirmDialogIcon from "../../../../assets/icons/ConfirmDialogIcon.svg";
import CourseService from "../../../../services/CourseService";
import AlertService from "../../../../services/AlertService";
import ExpertSessionsService from "../../../../services/ExpertSessionsService";
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaRegEdit } from "react-icons/fa";
import SharedService from "../../../../services/SharedService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";

const ExpertSessions = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState("");
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelSessionId, setCancelSessionId] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const registerControllerRef = useRef(null);

 const loadCourses = useCallback(async (signal) => {
    if (!authContext?.token) return;
    try {
      const data = await CourseService.GetCoursesByUser(
        authContext,
        signal  
      );

      if (signal?.aborted) return; 

      if (data?.isSuccess) {
        setCourses(data.data.map((c) => ({ label: c.label, value: c.value })));
      } else {
        AlertService.error(data?.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error(err);
      AlertService.error("Failed to load courses");
    }
  }, [authContext]);

  useEffect(() => {
    const expertSessionAccess = authContext.getPermissions("dashboard");
    if (expertSessionAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: expertSessionAccess.some((f) => f.access.includes(2)),
      showEdit: expertSessionAccess.some((f) => f.access.includes(3)),
      showDelete: expertSessionAccess.some((f) => f.access.includes(4)),
    });
  }, [authContext, navigate]);

const loadSessions = useCallback(async (signal) => { 
    if (!authContext?.token) return;
    try {
      setLoadingSessions(true);
      const courseId = selectedCourse ? selectedCourse : 0;
      const data = await ExpertSessionsService.GetCourseId(
        courseId,
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (data?.isSuccess) {
        setSessions(data.data || []);
      } else {
        AlertService.error(data?.errorMessage || "Failed to load sessions");
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      console.error("Session API error:", error);
      AlertService.error("Something went wrong");
    } finally {
      if (!signal?.aborted) setLoadingSessions(false); 
    }
  }, [selectedCourse, authContext]);

useEffect(() => {
    const controller = new AbortController();
    if (authContext?.token) loadCourses(controller.signal);
    return () => controller.abort();
  }, [authContext?.token, loadCourses]);

useEffect(() => {
    const controller = new AbortController();
    loadSessions(controller.signal);
    return () => controller.abort();
  }, [loadSessions]);

const formatDate = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue + "Z");
  const d = date.getDate().toString().padStart(2, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const y = date.getFullYear();

  return `${d}-${m}-${y}`;
};

const formatTimeRange = (startDate, endDate) => {
  if (!startDate) return "—";

  const start = new Date(startDate + "Z");
  const end = endDate ? new Date(endDate + "Z") : null;

  const options = { hour: "2-digit", minute: "2-digit", hour12: true };

  const startTime = start.toLocaleTimeString("en-IN", options).toUpperCase();
  const endTime = end ? end.toLocaleTimeString("en-IN", options).toUpperCase() : "";

  return `${startTime}${endTime ? ` - ${endTime}` : ""}`;
};

  const handleRegisterConfirm = (id) => {
    setSelectedSessionId(id);
    setShowConfirm(true);
  };



const handleRegister = async () => {
  if (isRegistering) return;

  if (registerControllerRef.current) {
    registerControllerRef.current.abort();
  }
  const controller = new AbortController();
  registerControllerRef.current = controller;

  setIsRegistering(true);

  try {
    const data = await ExpertSessionsService.Register(
      selectedSessionId,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data?.isSuccess) {
      AlertService.success("Registered successfully!");
      loadSessions();
      setShowConfirm(false);
    } else {
      AlertService.error(data?.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    console.error(err);
    AlertService.error("Registration failed");
  } finally {
    if (!controller.signal.aborted) setIsRegistering(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    registerControllerRef.current?.abort();
  };
}, []);

  const handleCancelConfirm = async () => {
     if (isCancelling) return;
    try {
       setIsCancelling(true);
      const data = await ExpertSessionsService.Cancel(
        cancelSessionId,
        authContext,
      );

      if (data?.isSuccess) {
        AlertService.success("Registration cancelled");
        loadSessions();
        setShowCancelConfirm(false);
      } else {
        AlertService.error(data?.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Cancel failed");
    }
    finally {
    setIsCancelling(false);
  }
  };

  return (
    <div className="container-fluid mt-3 pb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-semi-bold fs-5">Let's Talk to the Experts</h2>
        <div className="d-flex align-items-center gap-3">
          {/* Add Button */}
          {permissions?.showAdd && (
            <button
              className="btn btn-primary px-3"
              onClick={() => navigate("/expert-sessions/add")}
            >
              <FaPlus className="mb-1" /> Add Session
            </button>
          )}

          <div style={{ width: "250px" }}>
            <select
              className="form-select dropdown"
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
            >
              <option value="">All Courses</option>
              {courses.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      <div className="row g-4">
        {loadingSessions ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="col-12 col-lg-6 d-flex">
              <div
                className="card border-0 rounded-4 shadow p-3 p-lg-5 w-100"
                style={{ border: "1px solid #0D3744" }}
              >
                <div className="row align-items-center">
                  <div className="col-md-4 text-center mb-3 mb-md-0">
                    <Skeleton width="100%" height="200px" />
                  </div>
                  <div className="col-md-8">
                    <Skeleton width="70%" height="25px" className="mb-3" />
                    <Skeleton width="50%" height="20px" className="mb-2" />
                    <Skeleton width="50%" height="20px" className="mb-2" />
                    <Skeleton width="60%" height="20px" className="mb-4" />
                    <div className="d-flex gap-2">
                      <Skeleton width="100px" height="35px" />
                      <Skeleton width="120px" height="35px" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : sessions.length === 0 ? (
          <div className="text-center py-5 text-muted">No sessions found</div>
        ) : (
          sessions.map((item) => {
            const isRegistered = item.isRegistered;
            const instructorNames = item.instructors || [];

            return (
              <div key={item.id} className="col-12 col-lg-6 d-flex">
                <div
                  className="card rounded-4 shadow p-3 p-lg-5 h-100 d-flex flex-column w-100 position-relative card-gradient"
                >
                  {/* Edit Icon */}
                  {permissions.showEdit && (
                    <div
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        sessionStorage.setItem("expertSessionId", item.id);
                        navigate(
                          `/expert-sessions/edit/${SharedService.GenerateSlug(item.title)}`,
                          { state: { id: item.id } },
                        );
                      }}
                    >
                      <FaRegEdit size={20} />
                    </div>
                  )}

                  <div className="row align-items-center flex-grow-1">
                    {/* Image */}
                    <div className="col-md-4 text-center mb-3 mb-md-0">
                      <img
                        src={MeetExpertImage}
                        alt="Meet Expert"
                        className="img-fluid"
                        style={{ maxHeight: "200px" }}
                      />
                    </div>

                    {/* Content */}
                    <div className="col-md-8 d-flex flex-column">
                      <h5 className="fw-medium mb-3 text-center text-md-start">
                        {item.title}
                      </h5>

                      <p className="mb-2">
                        <span className="text-dark-gray">Course:</span>{" "}
                        <span className="fw-medium">{item.courseName}</span>
                      </p>

                      {instructorNames.length > 0 && (
                        <p className="mb-2">
                          <span className=" text-dark-gray">
                             {instructorNames?.length > 1 ? "Instructors" : "Instructor"}:
                          </span>{" "}
                             <span className="fw-medium">
      {instructorNames.join(", ")}
    </span>
                        </p>
                      )}

          <p className="mb-2">
  <span className="text-dark-gray">Date:</span>{" "}
  <span className="fw-medium">
    {formatDate(item.scheduledStartDate)}
  </span>
</p>

<p className="mb-4">
  <span className="text-dark-gray">Time:</span>{" "}
  <span className="fw-medium">
    {formatTimeRange(
      item.scheduledStartDate,
      item.scheduledEndDate
    )}
  </span>
</p>
                      <div className="mt-auto d-flex gap-3 justify-content-center justify-content-md-start">
                        {isRegistered ? (
                          <>
                            <button
                              className="btn btn-outline-secondary px-3 px-md-4 py-2 rounded-3"
                              onClick={() => {
                                setCancelSessionId(item.id);
                                setShowCancelConfirm(true);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-expert-session px-3 px-md-5 py-2 rounded-3 text-white"
                              onClick={() =>
                               navigate(
  `/expert-sessions/${SharedService.GenerateSlug(item.title)}`,
  { state: { sessionId: item.id } }
)
                              }
                            >
                              Join
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-expert-session px-3 px-md-5 py-2 rounded-3 text-white"
                            onClick={() => handleRegisterConfirm(item.id)}
                          >
                            Register
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <ConfirmDialogBox
          visible={showConfirm}
          onHide={() => setShowConfirm(false)}
          onConfirm={handleRegister}
          title="Would you like to register for this session?"
          message=""
          icon={ConfirmDialogIcon}
          loading={isRegistering}  
        />
      )}
      {showCancelConfirm && (
        <ConfirmDialogBox
          visible={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelConfirm}
          title="Are you sure you want to cancel this session?"
          message=""
          icon={ConfirmDialogIcon}
            loading={isCancelling}
        />
      )}
    </div>
  );
};

export default ExpertSessions;
